// src/pages/TalentDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import supabase from "../supabaseClient";

const bucket = {
  avatar: "avatars",
  banner: "banners",
  media: "talent-media",
  docs: "talent-docs",
};

async function ensureProfile() {
  const { data: { user }, error: uerr } = await supabase.auth.getUser();
  if (uerr || !user) throw uerr || new Error("No user");
  const { data, error } = await supabase
    .from("talent_profiles")
    .select("*")
    .eq("user_id", user.id)
    .single();
  if (error && error.code !== "PGRST116") throw error;
  if (!data) {
    const { data: created, error: cerr } = await supabase
      .from("talent_profiles")
      .insert({ user_id: user.id, full_name: user.user_metadata?.name || "" })
      .select("*")
      .single();
    if (cerr) throw cerr;
    return created;
  }
  return data;
}

function filePath(userId, filename) {
  const safe = filename.replace(/\s+/g, "_");
  return `${userId}/${crypto.randomUUID()}_${safe}`;
}

const Uploader = ({ accept, onPick }) => (
  <input
    type="file"
    accept={accept}
    onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
  />
);

export default function TalentDashboard() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [media, setMedia] = useState([]);
  const [docs, setDocs] = useState([]);
  const [refs, setRefs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shareToken, setShareToken] = useState(null);

  const skillsText = useMemo(
    () => (Array.isArray(profile?.skills) ? profile.skills.join(", ") : ""),
    [profile]
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = await ensureProfile();
        setProfile(p);

        const [{ data: m }, { data: d }, { data: r }] = await Promise.all([
          supabase.from("talent_media").select("*").eq("profile_id", p.id).order("created_at", { ascending: false }),
          supabase.from("talent_documents").select("*").eq("profile_id", p.id).order("created_at", { ascending: false }),
          supabase.from("talent_references").select("*").eq("profile_id", p.id).order("created_at", { ascending: false }),
        ]);
        setMedia(m || []);
        setDocs(d || []);
        setRefs(r || []);
      } catch (e) {
        console.error("Init failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function saveProfile(patch) {
    if (!profile) return;
    setSaving(true);
    const next = { ...profile, ...patch };
    const { data, error } = await supabase
      .from("talent_profiles")
      .update(next)
      .eq("id", profile.id)
      .select("*")
      .single();
    setSaving(false);
    if (error) return console.error("Save failed", error);
    setProfile(data);
  }

  async function uploadTo(bucketName, file) {
    const { data: { user } } = await supabase.auth.getUser();
    const path = filePath(user.id, file.name);
    const { error: upErr } = await supabase.storage.from(bucketName).upload(path, file, { upsert: false });
    if (upErr) throw upErr;
    return path;
  }

  async function setAvatar(file) {
    try {
      const path = await uploadTo(bucket.avatar, file);
      await saveProfile({ avatar_url: path });
    } catch (e) { console.error(e); }
  }

  async function setBanner(file) {
    try {
      const path = await uploadTo(bucket.banner, file);
      await saveProfile({ banner_url: path });
    } catch (e) { console.error(e); }
  }

  async function addMedia(file, kind) {
    if (!profile) return;
    try {
      const path = await uploadTo(bucket.media, file);
      const { data, error } = await supabase
        .from("talent_media")
        .insert({ profile_id: profile.id, kind, storage_path: path, title: file.name })
        .select("*").single();
      if (error) throw error;
      setMedia((cur) => [data, ...cur]);
    } catch (e) { console.error(e); }
  }

  async function addDoc(file) {
    if (!profile) return;
    try {
      const path = await uploadTo(bucket.docs, file);
      const { data, error } = await supabase
        .from("talent_documents")
        .insert({ profile_id: profile.id, storage_path: path, filename: file.name })
        .select("*").single();
      if (error) throw error;
      setDocs((cur) => [data, ...cur]);
    } catch (e) { console.error(e); }
  }

  async function toggleShare(row, table) {
    const next = { ...row, share: !row.share };
    const { data, error } = await supabase.from(table).update({ share: next.share }).eq("id", row.id).select("*").single();
    if (error) return console.error(error);
    if (table === "talent_media") setMedia((cur) => cur.map((m) => m.id === row.id ? data : m));
    if (table === "talent_documents") setDocs((cur) => cur.map((d) => d.id === row.id ? data : d));
    if (table === "talent_references") setRefs((cur) => cur.map((r) => r.id === row.id ? data : r));
  }

  async function deleteRow(row, table) {
    const { error } = await supabase.from(table).delete().eq("id", row.id);
    if (error) return console.error(error);
    if (table === "talent_media") setMedia((cur) => cur.filter((m) => m.id !== row.id));
    if (table === "talent_documents") setDocs((cur) => cur.filter((d) => d.id !== row.id));
    if (table === "talent_references") setRefs((cur) => cur.filter((r) => r.id !== row.id));
  }

  function publicUrl(bucketName, path) {
    // For avatars/banners (public). For private buckets, use signed URL when previewing self.
    const { data } = supabase.storage.from(bucketName).getPublicUrl(path);
    return data.publicUrl;
  }

  async function signUrl(bucketName, path) {
    const { data, error } = await supabase.storage.from(bucketName).createSignedUrl(path, 60 * 60);
    if (error) { console.error(error); return ""; }
    return data.signedUrl;
  }

  async function shareProfile() {
    // Issues a token (valid 30 days) and returns a URL fragment you can route to /share/:token
    const { data, error } = await supabase.rpc("issue_share_token", {
      p_subject_type: "talent",
      p_subject_id: profile.id,
      p_ttl: "30 days"
    });
    if (error) return console.error(error);
    setShareToken(data);
  }

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  return (
    <div className="p-6 grid grid-cols-12 gap-6">
      {/* LEFT: Profile editor */}
      <div className="col-span-12 lg:col-span-8 space-y-6">
        <div className="p-4 rounded-2xl shadow border">
          <h2 className="text-xl font-semibold mb-3">Identity</h2>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-sm mb-1">Avatar</div>
              <Uploader accept="image/*" onPick={setAvatar} />
              {profile?.avatar_url && (
                <img
                  src={publicUrl(bucket.avatar, profile.avatar_url)}
                  alt="avatar"
                  className="mt-2 w-24 h-24 object-cover rounded-full"
                />
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm mb-1">Banner</div>
              <Uploader accept="image/*" onPick={setBanner} />
              {profile?.banner_url && (
                <img
                  src={publicUrl(bucket.banner, profile.banner_url)}
                  alt="banner"
                  className="mt-2 w-full h-28 object-cover rounded-xl"
                />
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <input
              className="border rounded-lg p-2"
              placeholder="Full name"
              value={profile?.full_name || ""}
              onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))}
            />
            <input
              className="border rounded-lg p-2"
              placeholder="Headline"
              value={profile?.headline || ""}
              onChange={(e) => setProfile((p) => ({ ...p, headline: e.target.value }))}
            />
            <textarea
              className="col-span-2 border rounded-lg p-2"
              rows={3}
              placeholder="About"
              value={profile?.about || ""}
              onChange={(e) => setProfile((p) => ({ ...p, about: e.target.value }))}
            />
            <input
              className="col-span-2 border rounded-lg p-2"
              placeholder="Skills (comma separated)"
              defaultValue={skillsText}
              onBlur={(e) => saveProfile({ skills: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!profile?.public_view_enabled}
                onChange={(e) => saveProfile({ public_view_enabled: e.target.checked })}
              />
              Public profile enabled
            </label>

            <button
              onClick={shareProfile}
              className="ml-auto px-3 py-2 rounded-xl border shadow text-sm"
            >
              Create share-link token
            </button>
            {shareToken && (
              <code className="text-xs">
                /share/{shareToken}
              </code>
            )}

            <button
              disabled={saving}
              onClick={() => saveProfile({})}
              className="px-3 py-2 rounded-xl bg-black text-white text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* Media */}
        <div className="p-4 rounded-2xl shadow border">
          <h2 className="text-xl font-semibold mb-3">Media</h2>
          <div className="flex gap-3 items-center">
            <Uploader accept="image/*" onPick={(f) => addMedia(f, "image")} />
            <Uploader accept="video/*" onPick={(f) => addMedia(f, "video")} />
          </div>
          <ul className="mt-4 grid md:grid-cols-2 gap-3">
            {media.map((m) => (
              <li key={m.id} className="border rounded-xl p-3 flex items-center gap-3">
                <div className="text-sm flex-1">
                  <div className="font-medium">{m.title}</div>
                  <div className="opacity-60 text-xs">{m.kind}</div>
                </div>
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={!!m.share} onChange={() => toggleShare(m, "talent_media")} />
                  share
                </label>
                <button className="text-xs underline" onClick={() => deleteRow(m, "talent_media")}>delete</button>
              </li>
            ))}
          </ul>
        </div>

        {/* Documents */}
        <div className="p-4 rounded-2xl shadow border">
          <h2 className="text-xl font-semibold mb-3">Documents</h2>
          <Uploader accept=".pdf,.doc,.docx,.txt" onPick={addDoc} />
          <ul className="mt-4 grid md:grid-cols-2 gap-3">
            {docs.map((d) => (
              <li key={d.id} className="border rounded-xl p-3 flex items-center gap-3">
                <div className="text-sm flex-1">
                  <div className="font-medium">{d.filename}</div>
                </div>
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={!!d.share} onChange={() => toggleShare(d, "talent_documents")} />
                  share
                </label>
                <button className="text-xs underline" onClick={() => deleteRow(d, "talent_documents")}>delete</button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* RIGHT: Social + References */}
      <div className="col-span-12 lg:col-span-4 space-y-6">
        <div className="p-4 rounded-2xl shadow border">
          <h3 className="font-semibold mb-3">Social Links</h3>
          <SocialEditor profile={profile} onSave={(next) => saveProfile({ social_links: next })} />
        </div>

        <div className="p-4 rounded-2xl shadow border">
          <h3 className="font-semibold mb-3">References</h3>
          <ReferenceEditor
            items={refs}
            onCreate={async (ref) => {
              const { data, error } = await supabase.from("talent_references")
                .insert({ ...ref, profile_id: profile.id })
                .select("*").single();
              if (!error) setRefs((cur) => [data, ...cur]);
            }}
            onToggle={(r) => toggleShare(r, "talent_references")}
            onDelete={(r) => deleteRow(r, "talent_references")}
            onUploadPdf={async (r, file) => {
              const path = await uploadTo(bucket.docs, file);
              const { data, error } = await supabase.from("talent_references")
                .update({ uploaded_pdf_path: path })
                .eq("id", r.id).select("*").single();
              if (!error) setRefs((cur) => cur.map((x) => x.id === r.id ? data : x));
            }}
          />
        </div>
      </div>
    </div>
  );
}

function SocialEditor({ profile, onSave }) {
  const [items, setItems] = useState(Array.isArray(profile?.social_links) ? profile.social_links : []);
  useEffect(() => setItems(Array.isArray(profile?.social_links) ? profile.social_links : []), [profile?.social_links]);

  function addBlank() {
    setItems((cur) => [...cur, { type: "", url: "", share: true }]);
  }
  function update(i, patch) {
    setItems((cur) => cur.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function remove(i) {
    setItems((cur) => cur.filter((_, idx) => idx !== i));
  }
  return (
    <div>
      <button className="text-sm px-3 py-2 rounded-xl border shadow" onClick={addBlank}>Add link</button>
      <ul className="mt-3 space-y-2">
        {items.map((row, i) => (
          <li key={i} className="border rounded-xl p-3 grid grid-cols-6 gap-2 items-center">
            <input className="col-span-2 border rounded p-2 text-sm" placeholder="Type (LinkedIn, GitHub…)"
              value={row.type} onChange={(e) => update(i, { type: e.target.value })} />
            <input className="col-span-3 border rounded p-2 text-sm" placeholder="https://"
              value={row.url} onChange={(e) => update(i, { url: e.target.value })} />
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={!!row.share} onChange={() => update(i, { share: !row.share })} />
              share
            </label>
            <button className="text-xs underline" onClick={() => remove(i)}>remove</button>
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <button className="px-3 py-2 rounded-xl bg-black text-white text-sm" onClick={() => onSave(items)}>Save links</button>
      </div>
    </div>
  );
}

function ReferenceEditor({ items, onCreate, onToggle, onDelete, onUploadPdf }) {
  const [draft, setDraft] = useState({ name: "", phone: "", email: "", notes: "", share: true });
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <input className="border rounded p-2 text-sm" placeholder="Name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
        <input className="border rounded p-2 text-sm" placeholder="Phone" value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} />
        <input className="col-span-2 border rounded p-2 text-sm" placeholder="Email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
        <textarea className="col-span-2 border rounded p-2 text-sm" rows={2} placeholder="Notes" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={!!draft.share} onChange={() => setDraft((d) => ({ ...d, share: !d.share }))} />
          share
        </label>
        <button className="justify-self-end px-3 py-2 rounded-xl border shadow text-sm" onClick={() => {
          if (!draft.name) return;
          onCreate(draft);
          setDraft({ name: "", phone: "", email: "", notes: "", share: true });
        }}>Add reference</button>
      </div>

      <ul className="mt-4 space-y-2">
        {items.map((r) => (
          <li key={r.id} className="border rounded-xl p-3">
            <div className="flex items-center gap-2">
              <div className="font-medium">{r.name}</div>
              <span className="text-xs opacity-60">{r.email || r.phone}</span>
              <label className="ml-auto text-xs flex items-center gap-1">
                <input type="checkbox" checked={!!r.share} onChange={() => onToggle(r)} />
                share
              </label>
              <button className="text-xs underline" onClick={() => onDelete(r)}>delete</button>
            </div>
            {r.notes && <div className="text-sm mt-1">{r.notes}</div>}
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs opacity-70">Attach PDF letter:</span>
              <input type="file" accept=".pdf" onChange={(e) => e.target.files?.[0] && onUploadPdf(r, e.target.files[0])} />
              {r.uploaded_pdf_path && <span className="text-xs opacity-70">uploaded</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
